/**
 * Derive a favicon URL for an organisation from its website, using Google's
 * public S2 favicon service. Returns null when there is no usable website so
 * the caller can fall back to an explicit default (uploaded logo / generic
 * icon). The null result is an intentional UI default, not a bug-masking
 * fallback: an absent or malformed website simply has no derivable favicon.
 */
export function organisationFaviconUrl(website: string | undefined | null): string | null {
  if (!website) {
    return null;
  }
  const trimmed = website.trim();
  if (!trimmed) {
    return null;
  }
  const withProtocol = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  let hostname: string;
  try {
    hostname = new URL(withProtocol).hostname;
  } catch {
    // The stored website is not a parseable URL — no favicon can be derived.
    return null;
  }
  if (!hostname || !hostname.includes(".")) {
    return null;
  }
  // DuckDuckGo's icon service returns the image directly (HTTP 200, no
  // redirect). A non-redirecting URL is important for the browser *tab* icon:
  // Chrome follows redirects for an <img> but handles redirecting favicon
  // links unreliably, so the tab icon would often fail to update.
  return `https://icons.duckduckgo.com/ip3/${hostname}.ico`;
}

const MANAGED_FAVICON_ATTR = "data-organisation-favicon";

/**
 * Drive the browser tab favicon (`<link rel="icon">`) from the organisation's
 * website favicon, but only when one exists. A managed link element is added to
 * the document head when `href` is set, and removed when `href` is null so the
 * browser falls back to the platform's default favicon. The platform's own
 * icon link (if any) is never mutated — we only add/remove our own managed one.
 */
export function applyOrganisationDocumentFavicon(href: string | null): void {
  if (typeof document === "undefined") {
    return;
  }
  const head = document.head;
  // Always drop the previous managed link and append a fresh node. Browsers
  // (Chrome in particular) pick up a newly-created icon element far more
  // reliably than a mutated href on an existing one.
  head.querySelectorAll(`link[${MANAGED_FAVICON_ATTR}]`).forEach((link) => link.remove());
  if (!href) {
    return;
  }
  const link = document.createElement("link");
  link.setAttribute("rel", "icon");
  link.setAttribute(MANAGED_FAVICON_ATTR, "true");
  link.setAttribute("href", href);
  head.appendChild(link);
}
